'use client';

import {useState, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {Loader2, Store, LayoutDashboard, Users, ShieldCheck} from 'lucide-react';
import Link from 'next/link';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {useAuth} from '@/hooks/useAuth';
import {BusinessSlugFinder} from '@/components/auth/BusinessSlugFinder';

const loginSchema = z.object({
    email: z.string().email('Enter a valid email'),
    password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
    const {login, loginPending} = useAuth();
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm<LoginForm>({resolver: zodResolver(loginSchema)});

    const onSubmit = (data: LoginForm) => {
        login(data);
    };

    const inputCls = "h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40";

    return (
        <div className="min-h-screen bg-slate-50 text-slate-950">
            <div className="grid min-h-screen lg:grid-cols-[420px_1fr] xl:grid-cols-[480px_1fr]">

                {/* ── Left panel ── */}
                <section className="relative hidden overflow-hidden bg-[#13092e] lg:flex lg:flex-col">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_10%,rgba(167,139,250,0.35),transparent_55%),radial-gradient(ellipse_at_80%_90%,rgba(99,102,241,0.2),transparent_50%)]" />

                    <div className="relative z-10 flex flex-1 flex-col px-10 py-10">
                        {/* Logo */}
                        <div className="inline-flex items-center gap-3 self-start">
                            <img
                                src="/logo-u.png"
                                alt="UniPOS"
                                className="h-8 w-auto object-contain shrink-0"
                            />
                            <div className="flex flex-col">
                                <span className="font-mono text-xl font-bold tracking-tight text-white leading-none">
                                    Uni<span
                                        className="bg-gradient-to-r from-[#123D83] via-[#087DC1] to-[#08AFCB] bg-clip-text text-transparent"
                                        style={{
                                            backgroundImage: 'linear-gradient(to right, #123D83 0%, #087DC1 50%, #08AFCB 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                        }}
                                    >
                                        POS
                                    </span>
                                </span>
                                <span className="text-[7.5px] font-semibold tracking-[0.16em] text-white uppercase mt-0.5">
                                    ONE POS FOR EVERY BUSINESS
                                </span>
                            </div>
                        </div>

                        {/* Hero copy */}
                        <div className="mt-16">
                            <p className="text-xs font-semibold uppercase tracking-widest text-violet-400">
                                Platform login
                            </p>
                            <h1 className="mt-4 text-[2.25rem] font-semibold leading-[1.18] tracking-tight text-white">
                                Run your business from one dashboard.
                            </h1>
                            <p className="mt-5 text-[15px] leading-7 text-white/60">
                                Sign in to manage inventory, staff, and sales across every storefront from a single control center.
                            </p>
                        </div>

                        {/* Feature list */}
                        <ul className="mt-10 space-y-3">
                            {[
                                { icon: LayoutDashboard, text: 'Real-time sales dashboards' },
                                { icon: Users,           text: 'Manage staff and permissions' },
                                { icon: ShieldCheck,     text: 'Secure, role-based access' },
                            ].map((item) => (
                                <li
                                    key={item.text}
                                    className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.05] px-4 py-3"
                                >
                                    <item.icon className="h-[18px] w-[18px] shrink-0 text-violet-300" />
                                    <span className="text-sm text-white/80">{item.text}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Bottom tag */}
                        <div className="mt-auto">
                            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                                <p className="text-xs font-semibold uppercase tracking-widest text-violet-400">
                                    New to UniPOS?
                                </p>
                                <div className="mt-3 flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/20 text-violet-300">
                                        <Store className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-white">Register your business</p>
                                        <p className="text-xs text-white/35">Set up your storefront in minutes</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Right panel ── */}
                <section className="flex min-h-screen items-center justify-center px-4 py-10 sm:px-8 lg:py-16">
                    <div className="w-full max-w-[420px]">

                        {/* Mobile logo */}
                        <div className="mb-8 inline-flex items-center gap-3 lg:hidden">
                            <img
                                src="/logo-u.png"
                                alt="UniPOS"
                                className="h-8 w-auto object-contain shrink-0"
                            />
                            <div className="flex flex-col">
                                <span className="font-mono text-xl font-bold tracking-tight text-[#17233B] leading-none">
                                    Uni<span
                                        className="bg-gradient-to-r from-[#123D83] via-[#087DC1] to-[#08AFCB] bg-clip-text text-transparent"
                                        style={{
                                            backgroundImage: 'linear-gradient(to right, #123D83 0%, #087DC1 50%, #08AFCB 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                        }}
                                    >
                                        POS
                                    </span>
                                </span>
                                <span className="text-[7.5px] font-semibold tracking-[0.16em] text-slate-500 uppercase mt-0.5">
                                    ONE POS FOR EVERY BUSINESS
                                </span>
                            </div>
                        </div>

                        {/* Header */}
                        <div className="mb-7">
                            <p className="text-sm font-semibold text-violet-700">Platform login</p>
                            <h2 className="mt-1.5 text-3xl font-semibold tracking-tight text-slate-950">
                                Welcome back
                            </h2>
                            <p className="mt-2 text-sm leading-6 text-slate-500">
                                Sign in to manage your business, staff, and sales.
                            </p>
                        </div>

                        {/* Form card */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                            <form
                                method="POST"
                                action=""
                                noValidate
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    void handleSubmit(onSubmit)(e);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        void handleSubmit(onSubmit)();
                                    }
                                }}
                                className="space-y-5"
                            >

                                {/* Email */}
                                <div className="space-y-1.5">
                                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                                        Email
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="admin@bizpos.lk"
                                        autoComplete="email"
                                        className={inputCls}
                                        {...register('email')}
                                    />
                                    {errors.email && (
                                        <p className="text-xs text-red-600">{errors.email.message}</p>
                                    )}
                                </div>

                                {/* Password */}
                                <div className="space-y-1.5">
                                    <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                                        Password
                                    </Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        className={inputCls}
                                        {...register('password')}
                                    />
                                    {errors.password && (
                                        <p className="text-xs text-red-600">{errors.password.message}</p>
                                    )}
                                </div>

                                <div className="pt-1">
                                    <Button
                                        type="submit"
                                        disabled={!isMounted || loginPending}
                                        className="h-11 w-full rounded-xl bg-violet-600 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 focus-visible:ring-2 focus-visible:ring-violet-500/50 disabled:opacity-60"
                                    >
                                        {loginPending ? (
                                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in…</>
                                        ) : (
                                            'Sign in'
                                        )}
                                    </Button>
                                </div>

                                <p className="text-center text-xs text-slate-400">
                                    This login is for platform admins and business owners.
                                </p>
                            </form>
                        </div>

                        {/* Divider */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-slate-200" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-slate-50 px-3 text-xs text-slate-400">
                                    or access a business portal
                                </span>
                            </div>
                        </div>

                        {/* Business slug finder */}
                        <BusinessSlugFinder />

                        {/* Register link */}
                        <p className="mt-6 text-center text-sm text-slate-500">
                            New to UniPOS?{' '}
                            <Link
                                href="/register"
                                className="font-semibold text-violet-700 underline underline-offset-4 hover:text-violet-800"
                            >
                                Register your business →
                            </Link>
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}